/**
 * VectorAttributeTable — Card 4 §"Attribute table" requirement.
 *
 * Minimal token-driven HTML `<table>` per WP-4 blocker policy: ships
 * without the (planned) `<DwTable>` design-system primitive but stays
 * tokenized for radius / density / typography / surface levels.
 *
 * Keyboard contract:
 *   - `ArrowDown` / `ArrowUp` — move the focused row by ±1
 *   - `Home` / `End` — jump to the first / last row on the current page
 *   - `Enter` — emit `onSelect(feature)` for the focused row
 *   - `PageDown` / `PageUp` — move ±1 page
 *
 * The component is uncontrolled w.r.t. pagination; the active page
 * and page size live in local state because the WP-4 acceptance only
 * asks for "renders rows / paging", not server-side paging.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Icon, Stack, Text, tokens } from '@/design';
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

export type VectorAttributeRow = VectorExportFeature & {
  readonly _rowKey?: string;
};

export interface VectorAttributeTableProps {
  /** Pre-filtered features to render. */
  readonly features: ReadonlyArray<VectorAttributeRow>;
  /** Visible columns (sourced by the ToolCard from the feature property keys). */
  readonly columns: ReadonlyArray<string>;
  /** Optional id-column hint — these columns are rendered in mono. */
  readonly idColumns?: ReadonlyArray<string>;
  /** Total number of features (when paged source carries more than `features.length`). */
  readonly totalCount?: number;
  /** Active loading / error state. */
  readonly state?: 'idle' | 'loading' | 'error' | 'empty';
  /** Localized error message when `state === 'error'`. */
  readonly errorMessage?: string;
  readonly pageSize?: 10 | 25 | 50;
  /** Fires when a row is activated (click or Enter). */
  readonly onSelect?: (feature: VectorAttributeRow) => void;
  /** Currently selected feature id (for visual highlight). */
  readonly selectedFeatureId?: string | number | null;
  readonly 'data-testid'?: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export function VectorAttributeTable({
  features,
  columns,
  idColumns,
  totalCount,
  state = 'idle',
  errorMessage,
  pageSize: pageSizeProp = 10,
  onSelect,
  selectedFeatureId = null,
  'data-testid': testId,
}: VectorAttributeTableProps) {
  const headingId = useId();
  const tableId = useId();
  const [pageSize, setPageSize] = useState<10 | 25 | 50>(pageSizeProp);
  const [pageIndex, setPageIndex] = useState(0);
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const pageCount = Math.max(1, Math.ceil(features.length / pageSize));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const pageStart = safePageIndex * pageSize;
  const pageRows = features.slice(pageStart, pageStart + pageSize);
  const idColumnSet = useMemo(
    () => new Set<string>([...(idColumns ?? []), 'id']),
    [idColumns],
  );

  useEffect(() => {
    if (focusedRowIndex >= pageRows.length) {
      setFocusedRowIndex(Math.max(0, pageRows.length - 1));
    }
  }, [focusedRowIndex, pageRows.length]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTableSectionElement>) => {
      if (pageRows.length === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedRowIndex((prev) => Math.min(prev + 1, pageRows.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedRowIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Home':
          event.preventDefault();
          setFocusedRowIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedRowIndex(pageRows.length - 1);
          break;
        case 'PageDown':
          event.preventDefault();
          setPageIndex((prev) => Math.min(prev + 1, pageCount - 1));
          setFocusedRowIndex(0);
          break;
        case 'PageUp':
          event.preventDefault();
          setPageIndex((prev) => Math.max(prev - 1, 0));
          setFocusedRowIndex(0);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (onSelect) {
            const feature = pageRows[focusedRowIndex];
            if (feature) onSelect(feature);
          }
          break;
        default:
          break;
      }
    },
    [focusedRowIndex, onSelect, pageCount, pageRows],
  );

  const reportedTotal = totalCount ?? features.length;

  return (
    <section
      aria-labelledby={headingId}
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}
    >
      <Stack direction="row" align="center" gap={2}>
        <Icon name="table" size={20} decorative />
        <Text variant="subtitle" as="h3" id={headingId} style={{ margin: 0 }}>
          Attribute table
        </Text>
        <span style={{ flex: '1 1 auto' }} />
        <Text variant="caption" as="span">
          <strong>{reportedTotal}</strong> rows
        </Text>
      </Stack>

      {state === 'loading' ? (
        <Stack direction="row" gap={2} align="center" data-testid={`${testId}-loading`}>
          <Icon name="pending" size={20} decorative />
          <Text variant="body" as="span">Loading features…</Text>
        </Stack>
      ) : null}
      {state === 'error' ? (
        <Stack direction="row" gap={2} align="center" data-testid={`${testId}-error`}>
          <Icon name="error" size={20} decorative />
          <Text variant="body" as="span">
            {errorMessage ?? 'Failed to load features.'}
          </Text>
        </Stack>
      ) : null}
      {state === 'empty' || (state === 'idle' && features.length === 0) ? (
        <Stack direction="row" gap={2} align="center" data-testid={`${testId}-empty`}>
          <Icon name="info" size={20} decorative />
          <Text variant="body" as="span">No features match the current filter.</Text>
        </Stack>
      ) : null}

      {pageRows.length > 0 ? (
        <div
          role="region"
          aria-labelledby={headingId}
          style={{
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            borderRadius: tokens.radius.md,
            overflowX: 'auto',
            background: tokens.color.surface[1],
          }}
        >
          <table
            id={tableId}
            data-testid={`${testId}-table`}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.sm,
              color: tokens.color.ink.primary,
            }}
          >
            <caption
              style={{
                ...visuallyHiddenStyle(),
              }}
            >
              Vector layer feature attributes; use Arrow keys to move and Enter to select.
            </caption>
            <thead>
              <tr style={{ background: tokens.color.surface[2] }}>
                <th scope="col" style={headerCellStyle()}>
                  <span aria-hidden="true">#</span>
                </th>
                <th scope="col" style={headerCellStyle()}>id</th>
                {columns.map((column) => (
                  <th key={column} scope="col" style={headerCellStyle()}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              ref={tbodyRef}
              role="rowgroup"
              tabIndex={0}
              aria-label="Feature rows"
              aria-activedescendant={`row-${pageStart + focusedRowIndex}`}
              onKeyDown={handleKeyDown}
              style={{ outline: 'none' }}
            >
              {pageRows.map((feature, indexOnPage) => {
                const featureKey = feature._rowKey ?? String(feature.id ?? `${pageStart + indexOnPage}`);
                const isFocused = indexOnPage === focusedRowIndex;
                const isSelected =
                  selectedFeatureId !== null && String(selectedFeatureId) === String(feature.id);
                return (
                  <tr
                    id={`row-${pageStart + indexOnPage}`}
                    key={featureKey}
                    data-testid={`${testId}-row`}
                    role="row"
                    aria-selected={isSelected}
                    onClick={() => {
                      setFocusedRowIndex(indexOnPage);
                      if (onSelect) onSelect(feature);
                    }}
                    style={{
                      background: isSelected
                        ? tokens.color.accent.subtle
                        : isFocused
                          ? tokens.color.surface[3]
                          : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <td style={cellStyle({ mono: true })}>{pageStart + indexOnPage + 1}</td>
                    <td style={cellStyle({ mono: true })}>{formatId(feature.id)}</td>
                    {columns.map((column) => {
                      const raw = feature.properties?.[column];
                      const isIdColumn = idColumnSet.has(column);
                      return (
                        <td key={column} style={cellStyle({ mono: isIdColumn })}>
                          {formatCell(raw)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {features.length > 0 ? (
        <Stack direction="row" align="center" gap={2} justify="space-between">
          <Stack direction="row" align="center" gap={2}>
            <label
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.size.xs,
                color: tokens.color.ink.secondary,
                display: 'inline-flex',
                gap: tokens.space[1],
                alignItems: 'center',
              }}
            >
              Rows per page
              <select
                aria-label="Rows per page"
                value={pageSize}
                onChange={(event) => {
                  const next = Number(event.target.value) as 10 | 25 | 50;
                  setPageSize(next);
                  setPageIndex(0);
                  setFocusedRowIndex(0);
                }}
                style={selectStyle()}
              >
                {PAGE_SIZE_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </Stack>
          <Stack direction="row" align="center" gap={1}>
            <PageButton
              label="Previous page"
              icon="arrow_back"
              disabled={safePageIndex === 0}
              onClick={() => {
                setPageIndex((prev) => Math.max(prev - 1, 0));
                setFocusedRowIndex(0);
              }}
            />
            <Text variant="caption" as="span" data-testid={`${testId}-page`}>
              Page <strong>{safePageIndex + 1}</strong> / {pageCount}
            </Text>
            <PageButton
              label="Next page"
              icon="arrow_forward"
              disabled={safePageIndex >= pageCount - 1}
              onClick={() => {
                setPageIndex((prev) => Math.min(prev + 1, pageCount - 1));
                setFocusedRowIndex(0);
              }}
            />
          </Stack>
        </Stack>
      ) : null}
    </section>
  );
}

interface PageButtonProps {
  readonly label: string;
  readonly icon: 'arrow_back' | 'arrow_forward';
  readonly disabled: boolean;
  readonly onClick: () => void;
}

function PageButton({ label, icon, disabled, onClick }: PageButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        appearance: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: tokens.density.touchMin,
        height: tokens.density.touchMin,
        background: 'transparent',
        border: 0,
        color: tokens.color.ink.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        borderRadius: tokens.radius.sm,
      }}
    >
      <Icon name={icon} size={20} decorative />
    </button>
  );
}

function headerCellStyle() {
  return {
    textAlign: 'left',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xs,
    fontWeight: tokens.font.weight.medium,
    color: tokens.color.ink.secondary,
    paddingInline: tokens.space[2],
    paddingBlock: tokens.space[2],
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    whiteSpace: 'nowrap',
  } as const;
}

function cellStyle({ mono }: { mono: boolean }) {
  return {
    paddingInline: tokens.space[2],
    paddingBlock: tokens.space[2],
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[2]}`,
    fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    color: tokens.color.ink.primary,
    height: tokens.density.rowDefault,
    minWidth: tokens.layout.popoverSm,
    maxWidth: tokens.layout.popoverLg,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as const;
}

function selectStyle() {
  return {
    minHeight: tokens.density.rowDense,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[1],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function visuallyHiddenStyle() {
  return {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  } as const;
}

function formatId(value: string | number | undefined): string {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
